import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Package, ShoppingBag, Users, IndianRupee, FileText, Truck, Settings, Sparkles, Image as ImageIcon, Undo2, UserPlus } from 'lucide-react';import { toast } from 'sonner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-dashboard-heading">Admin Dashboard</h1>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigate('/admin/products')} data-testid="manage-products-button">Products</Button>
            <Button size="sm" onClick={() => navigate('/admin/orders')} variant="outline" data-testid="manage-orders-button">Orders</Button>
            <Button size="sm" onClick={() => navigate('/admin/cms')} variant="outline" data-testid="manage-content-button">CMS</Button>
            <Button size="sm" onClick={() => navigate('/admin/invoices')} variant="outline" data-testid="invoice-settings-button">
              <FileText className="h-4 w-4 mr-1" /> Invoices
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/manual-invoices')} variant="outline" data-testid="manual-invoices-button">
              <FileText className="h-4 w-4 mr-1" /> Tax Invoices
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/coupons')} variant="outline" data-testid="manage-coupons-button">
              Coupons
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/courier-rules')} variant="outline" data-testid="courier-rules-button">
              <Truck className="h-4 w-4 mr-1" /> Courier Rules
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/subscriptions')} variant="outline" data-testid="subscriptions-button">
              <Sparkles className="h-4 w-4 mr-1" /> Subscription Plans
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/gallery')} variant="outline" data-testid="gallery-button">
              <ImageIcon className="h-4 w-4 mr-1" /> Gallery
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/returns')} variant="outline" data-testid="returns-button">
              <Undo2 className="h-4 w-4 mr-1" /> Returns
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/affiliates')} variant="outline" data-testid="affiliates-button">
              <UserPlus className="h-4 w-4 mr-1" /> Affiliates
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/settings')} variant="outline" data-testid="platform-settings-button">
              <Settings className="h-4 w-4 mr-1" /> Settings
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <button
            type="button"
            onClick={() => navigate('/admin/revenue')}
            className="text-left p-5 rounded-xl bg-white border border-[#E0E0E0] cursor-pointer hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="stat-revenue"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Revenue</span>
              <IndianRupee className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-black text-primary">{'\u20B9'}{stats?.total_revenue?.toFixed(0) || '0'}</p>
            <p className="text-[11px] text-muted-foreground mt-1">View transactions →</p>
          </button>
          <div className="p-5 rounded-xl bg-white border border-[#E0E0E0]" data-testid="stat-orders">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Orders</span>
              <ShoppingBag className="h-4 w-4 text-secondary" />
            </div>
            <p className="text-2xl font-black">{stats?.total_orders || 0}</p>
          </div>
          <div className="p-5 rounded-xl bg-white border border-[#E0E0E0]" data-testid="stat-products">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Products</span>
              <Package className="h-4 w-4 text-accent" />
            </div>
            <p className="text-2xl font-black">{stats?.total_products || 0}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className="text-left p-5 rounded-xl bg-white border border-[#E0E0E0] cursor-pointer hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="stat-users"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Users</span>
              <Users className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-black">{stats?.total_users || 0}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Customers vs visitors →</p>
          </button>
        </div>

        {stats?.low_stock_products && stats.low_stock_products.length > 0 && (
          <div className="p-6 rounded-2xl bg-white border border-[#E0E0E0]">
            <h2 className="text-lg font-bold font-heading mb-4" data-testid="low-stock-heading">Low Stock Alert</h2>
            <div className="space-y-2">
              {stats.low_stock_products.map((product) => (
                <div key={product.product_id} className="flex justify-between items-center p-3 rounded-lg bg-muted" data-testid={`low-stock-${product.product_id}`}>
                  <div>
                    <p className="font-medium text-sm">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.category}</p>
                  </div>
                  <span className="text-destructive font-bold text-sm">{product.stock} left</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
